"use client";

import type { CaptureResult, PostHog, PostHogConfig } from "posthog-js";
import {
  availableFeatures, featureForPath, type AnalyticsConfig, type AnalyticsRole, type WorkflowEvent,
} from "./analytics-schema";

const EVENTS = new Set(["$identify", "$pageview", "$snapshot", "feature_available", "feature_exposed",
  "feature_opened", "script_editing_started", "workflow_started", "workflow_completed", "save_failed"]);
const SAFE_PROPERTIES = new Set([
  "token", "distinct_id", "$anon_distinct_id", "$device_id", "$session_id", "$window_id",
  "$lib", "$lib_version", "$browser", "$browser_version", "$os", "$os_version", "$device_type",
  "$screen_height", "$screen_width", "$viewport_height", "$viewport_width", "$is_identified",
  "$process_person_profile", "$snapshot_data", "$snapshot_bytes", "$sent_at",
  "feature_key", "user_role", "view_role", "workflow", "outcome", "workflow_id", "reason",
]);

/** The SDK adds URLs, titles, referrers and person properties of its own. */
export function sanitizeAnalyticsEvent(event: CaptureResult): CaptureResult | null {
  if (!EVENTS.has(event.event)) return null;
  const properties = Object.fromEntries(Object.entries(event.properties).filter(([key]) => SAFE_PROPERTIES.has(key)));
  const path = featureForPath(typeof event.properties.$pathname === "string" ? event.properties.$pathname : "/");
  properties.$current_url = `https://ironside.invalid${path}`;
  properties.$pathname = path;
  properties.environment = "production";
  properties.analytics_version = 1;
  properties.$geoip_disable = true;
  // Do not merge SDK-generated initial URLs into person profiles.
  delete event.$set;
  delete event.$set_once;
  delete event.$unset;
  return { ...event, properties };
}

export function analyticsOptions(config: AnalyticsConfig): Partial<PostHogConfig> {
  return {
    api_host: config.host,
    ui_host: config.host === "https://eu.i.posthog.com" ? "https://eu.posthog.com" : "https://us.posthog.com",
    persistence: "localStorage",
    save_referrer: false,
    save_campaign_params: false,
    disable_capture_url_hashes: true,
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    rageclick: false,
    capture_performance: false,
    capture_exceptions: false,
    disable_surveys: true,
    disable_web_experiments: true,
    enable_recording_console_log: false,
    disable_session_recording: !config.replay,
    respect_dnt: true,
    advanced_disable_flags: !config.replay,
    advanced_disable_feature_flags: true,
    before_send: (event) => identity ? sanitizeAnalyticsEvent(event) : null,
    session_recording: {
      sampleRate: 0.1,
      maskAllInputs: true,
      maskTextSelector: "*",
      maskAllElementAttributes: true,
      blockSelector: "[data-analytics-private], img, video, audio, canvas, iframe, input[type=hidden], input[type=file]",
      captureJsonLd: false,
      recordHeaders: false,
      recordBody: false,
      recordCrossOriginIframes: false,
      captureCanvas: { recordCanvas: false },
      // The recorder also uses this callback for page metadata (only `name`).
      // Network requests carry other fields and are discarded completely.
      maskCapturedNetworkRequestFn: (request) => {
        if (Object.keys(request).length !== 1 || typeof request.name !== "string") return null;
        try {
          return { ...request, name: `https://ironside.invalid${featureForPath(new URL(request.name).pathname)}` };
        } catch { return null; }
      },
    },
  };
}

let client: PostHog | undefined;
let loading: Promise<PostHog | undefined> | undefined;
let project: string | undefined;
let identity: string | undefined;
let role: AnalyticsRole | undefined;
let viewRole: AnalyticsRole | undefined;
let page: string | undefined;
let lastNavigation: string | undefined;
let session: string | undefined;
let generation = 0;
let instanceNumber = 0;
const available = new Set<string>();
const exposed = new Set<string>();
const editing = new Set<string>();
const resources = new Map<string, string>();

function resetCounters() {
  available.clear(); exposed.clear(); editing.clear(); resources.clear();
  lastNavigation = undefined;
}

function permitted(): boolean {
  return typeof window !== "undefined" && navigator.doNotTrack !== "1"
    && !(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
}

function capture(event: string, properties: Record<string, string> = {}) {
  try {
    if (!identity || !client || !permitted() || client.has_opted_out_capturing()) return;
    client.capture(event, { ...properties, user_role: role, view_role: viewRole, $pathname: page ?? "/other" });
  } catch { /* Analytics must never interrupt a user action. */ }
}

/** Cancel stale route effects and an SDK import that resolves after logout. */
export function cancelAnalyticsNavigation() { generation++; }

export async function trackAnalyticsPage(config: AnalyticsConfig | null, pathname: string, lensRole: AnalyticsRole) {
  const run = ++generation;
  if (!config || !permitted()) { clearAnalyticsIdentity(); return; }
  try {
    const key = `${config.host}:${config.token}`;
    if (!loading || project !== key) {
      client?.set_config({ disable_session_recording: true, advanced_disable_flags: true });
      client?.stopSessionRecording();
      client?.reset(true);
      client = undefined;
      project = key;
      loading = import("posthog-js").then(({ posthog }) => {
        if (project !== key) return undefined;
        return posthog.init(config.token, { ...analyticsOptions(config), disable_session_recording: true }, `ironside_${++instanceNumber}`);
      }).catch(() => undefined);
    }
    const loaded = await loading;
    if (!loaded || run !== generation) return;
    client = loaded;
    if (client.has_opted_out_capturing()) { clearAnalyticsIdentity(); return; }
    if (identity !== config.userId || client.get_distinct_id() !== config.userId) {
      client.stopSessionRecording();
      // Preserve a signed-in person's session across document reloads. Reset
      // only when the persisted identity belongs to another account/browser ID.
      if (client.get_distinct_id() !== config.userId) client.reset(true);
      resetCounters();
      identity = config.userId;
      client.identify(identity);
    }
    role = config.role;
    viewRole = lensRole;
    page = featureForPath(pathname);
    const nextSession = client.get_session_id();
    if (session !== nextSession) { resetCounters(); session = nextSession; }
    // Include the real pathname in the LOCAL dedupe key only. Different detail
    // pages count as navigation, but no identifiers leave the browser.
    const navigation = `${pathname}:${lensRole}`;
    if (lastNavigation !== navigation) {
      lastNavigation = navigation;
      capture("$pageview");
      capture("feature_opened", { feature_key: page });
    }
    for (const feature of availableFeatures(lensRole)) {
      const stamp = `${lensRole}:${feature}`;
      if (!available.has(stamp)) {
        available.add(stamp);
        capture("feature_available", { feature_key: feature });
      }
    }
    client.set_config({ disable_session_recording: !config.replay, advanced_disable_flags: !config.replay });
    if (!config.replay) client.stopSessionRecording();
  } catch { /* Missing storage, blocked scripts and SDK failures are harmless. */ }
}

export function clearAnalyticsIdentity() {
  generation++;
  identity = undefined;
  resetCounters();
  try {
    client?.set_config({ disable_session_recording: true, advanced_disable_flags: true });
    client?.stopSessionRecording(); client?.reset(true);
  } catch { /* no-op */ }
}

export function trackFeatureExposure(feature: string) {
  if (!identity || !viewRole || !availableFeatures(viewRole).includes(feature)) return;
  const stamp = `${viewRole}:${feature}`;
  if (exposed.has(stamp)) return;
  exposed.add(stamp);
  capture("feature_exposed", { feature_key: feature });
}

function resourceId(id: string): string {
  if (!resources.has(id)) resources.set(id, crypto.randomUUID());
  return resources.get(id)!;
}

export function trackScriptEditing(id: string) {
  if (!identity || editing.has(id)) return;
  try {
    editing.add(id);
    capture("script_editing_started", { workflow_id: resourceId(id) });
  } catch { /* no-op */ }
}

export function trackWorkflow(phase: "started" | "completed", id: string, event: WorkflowEvent) {
  if (!identity) return;
  try { capture(`workflow_${phase}`, { workflow: event.workflow, outcome: event.outcome, workflow_id: resourceId(id) }); } catch { /* no-op */ }
}

export function trackSaveFailure(status?: number) {
  capture("save_failed", { reason: status === undefined ? "network" : status === 409 ? "conflict" : status >= 500 ? "server" : "refused" });
}
